/**
 * Mudar a coluna de lugar arrastando o cabeçalho (SPEC-20261103-1217).
 *
 * Pedido do Cristian, 2026-11-03: "tanto no quadro, como na lista não está
 * sendo possível mudar a coluna de lugar". As duas tabelas usavam o arraste
 * NATIVO do navegador (`draggable` + dragover/drop), que é frágil justamente
 * aqui: o cabeçalho inteiro é um botão de ordenar, a caixa da tabela rola
 * arrastando, e o WebView do Desktop trata o arraste nativo à parte. O gesto
 * agora é feito com eventos de ponteiro, como o da largura da coluna — o mesmo
 * em todo lugar.
 *
 * Abaixo de LIMIAR px ainda é clique (ordenar continua valendo). Depois de
 * arrastar, o clique que o navegador dispara ao soltar é comido — senão
 * ordenaria pela coluna que acabou de mudar de lugar.
 */
export const LIMIAR_DO_ARRASTE_DE_COLUNA = 5

/** Classe no `<body>` durante o gesto: cursor de mão fechada, sem seleção de texto. */
export const CLASSE_ARRASTANDO_COLUNA = 'ob-arrastando-coluna'

/** Atributo que cada cabeçalho carrega com a chave da sua coluna. */
export const ATRIBUTO_DA_COLUNA = 'data-coluna'

type InicioDoGesto = {
  button: number
  clientX: number
  clientY: number
  target: EventTarget | null
}

export type GestoDeColuna = {
  /** Coluna pega. */
  chave: string
  /** Só cabeçalhos DENTRO desta caixa contam como destino. */
  caixa: HTMLElement | null
  /** Destino aceito? (o Título, por exemplo, não recebe ninguém na frente dele) */
  aceita?: (destino: string) => boolean
  /** Elementos com gesto próprio dentro do cabeçalho (alça de largura, menu). */
  ignorar?: string
  onInicio: (chave: string) => void
  onSobre: (destino: string | null) => void
  onSoltar: (destino: string) => void
  onFim: () => void
}

/** A coluna sob o ponteiro, dentro da caixa — ou null. */
export function colunaSobOPonteiro(caixa: HTMLElement, x: number, y: number): string | null {
  const el = document.elementFromPoint(x, y)?.closest(`[${ATRIBUTO_DA_COLUNA}]`)
  if (!el || !caixa.contains(el)) return null
  return el.getAttribute(ATRIBUTO_DA_COLUNA)
}

export function iniciarArrasteDeColuna(e: InicioDoGesto, gesto: GestoDeColuna) {
  if (e.button !== 0 || !gesto.caixa) return
  const alvo = e.target as HTMLElement | null
  if (gesto.ignorar && alvo?.closest?.(gesto.ignorar)) return
  const caixa = gesto.caixa
  const x0 = e.clientX
  const y0 = e.clientY
  let arrastou = false
  let sobre: string | null = null

  const mover = (ev: PointerEvent) => {
    if (!arrastou) {
      if (Math.hypot(ev.clientX - x0, ev.clientY - y0) < LIMIAR_DO_ARRASTE_DE_COLUNA) return
      arrastou = true
      document.body.classList.add(CLASSE_ARRASTANDO_COLUNA)
      window.getSelection()?.removeAllRanges()
      gesto.onInicio(gesto.chave)
    }
    ev.preventDefault()
    const k = colunaSobOPonteiro(caixa, ev.clientX, ev.clientY)
    const valido = k !== null && k !== gesto.chave && (gesto.aceita?.(k) ?? true) ? k : null
    if (valido !== sobre) {
      sobre = valido
      gesto.onSobre(valido)
    }
  }
  const soltar = (ev: PointerEvent) => {
    window.removeEventListener('pointermove', mover)
    window.removeEventListener('pointerup', soltar)
    window.removeEventListener('pointercancel', soltar)
    if (!arrastou) return
    document.body.classList.remove(CLASSE_ARRASTANDO_COLUNA)
    const comer = (c: Event) => {
      c.stopPropagation()
      c.preventDefault()
    }
    window.addEventListener('click', comer, { capture: true, once: true })
    // se o clique não vier (soltou fora), desarma na volta do frame
    setTimeout(() => window.removeEventListener('click', comer, { capture: true }), 0)
    const destino = ev.type === 'pointerup' ? sobre : null
    gesto.onFim()
    if (destino) gesto.onSoltar(destino)
  }
  window.addEventListener('pointermove', mover, { passive: false })
  window.addEventListener('pointerup', soltar)
  window.addEventListener('pointercancel', soltar)
}
